import {
  Card,
  CardBody,
  CardColors,
  CardDetails,
  CardSubtitle,
  CardSummary,
} from "$components/card";
import { DispensedPrescriptionFooterItems } from "$routes/patient/_lib/dispensation-states/dispensed-prescription";
import { ExpiredPrescriptionFooterItems } from "$routes/patient/_lib/dispensation-states/expired-prescription";
import { LegacyPrescription } from "$routes/patient/_lib/dispensation-states/legacy-prescription";
import { PaperPrescriptionFooterItems } from "$routes/patient/_lib/dispensation-states/paper-prescription";
import { UndispensedPrescriptionFooterItems } from "$routes/patient/_lib/dispensation-states/undispensed-prescription";
import type { HistoricAncPrescription } from "$routes/patient/[:patientId]/antenatal-care/_lib/anc-encounters";
import { styled, Typography } from "@material-ui/core";
import { FormattedMessage } from "react-intl";
import { AncPrescriptionDetails } from "./anc-prescription-details";
import {
  getPrescriptionDispensationState,
  PRESCRIPTION_STATES,
} from "./anc-prescription-utils";

const Footer = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(3),
  paddingTop: theme.spacing(1),
  "&[data-active=true]": {
    // These allow the footer background to bleed past the card body and appear to fill the whole
    // of the bottom of the card
    margin: theme.spacing(1, -2, -2),
    paddingLeft: theme.spacing(2),
    paddingBlock: theme.spacing(0.5),
    background: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  },
}));

const NoteContainer = styled("div")(({ theme }) => ({
  background: "#E9FBFB",
  margin: `0 ${theme.spacing(-2)}px`,
  padding: theme.spacing(2),
}));

const NoteBody = styled(Typography)({
  fontSize: 11,
  marginTop: 10,
  lineHeight: 1.5,
});

type LoadedLocations = {
  locations: { _id: string; name: string }[];
  loading: false;
};

type LoadingLocations = {
  locations: undefined;
  loading: true;
};

type LocationsData = LoadedLocations | LoadingLocations;

export const AncPrescriptionCard: FC<{
  prescription: HistoricAncPrescription;
  locationsData?: LocationsData;
}> = ({ prescription, locationsData = { locations: [], loading: false } }) => {
  const prescriptionHasNote = !!prescription.note;
  const prescriptionState = getPrescriptionDispensationState(prescription);

  const dispensation = prescription.dispensations?.items[0];
  const dispensedBy = [
    dispensation?.dispensedByUser?.person?.firstname,
    dispensation?.dispensedByUser?.person?.lastname,
  ].join(" ");

  // Extract prescriber info from the prescription
  const prescribedBy = prescription.prescriber?.person
    ? `${prescription.prescriber.person.firstname} ${prescription.prescriber.person.lastname}`
    : "";
  const prescribedAt = new Date(prescription.startsAt);
  const locationName = "";

  return (
    <Card
      key={prescription.id}
      color={
        prescriptionState === PRESCRIPTION_STATES.PAPER_PRESCRIPTION_ISSUED
          ? CardColors.DISABLED
          : CardColors.PRESCRIPTION
      }
      data-testid="patient.history.diagnosis.prescription"
      expandable={prescriptionHasNote}
    >
      <CardBody>
        <CardSummary>
          <CardSubtitle
            color={
              prescriptionState ===
              PRESCRIPTION_STATES.PAPER_PRESCRIPTION_ISSUED
                ? CardColors.DISABLED
                : CardColors.PRESCRIPTION
            }
            gutterBottom
          >
            <FormattedMessage
              id="patient.history.prescription.title"
              defaultMessage="Prescription"
            />
          </CardSubtitle>
          <AncPrescriptionDetails
            prescription={prescription}
            prescribedBy={prescribedBy}
            prescribedAt={prescribedAt}
            locationName={locationName}
            locationsData={locationsData}
          />
        </CardSummary>
        {prescriptionHasNote && (
          <CardDetails>
            {prescriptionHasNote && (
              <NoteContainer>
                <Typography variant="body2">
                  <strong>
                    <FormattedMessage
                      id="patient.history.prescription.notes"
                      defaultMessage="Prescription notes"
                    />
                  </strong>
                </Typography>
                <NoteBody variant="body2">{prescription.note}</NoteBody>
              </NoteContainer>
            )}
          </CardDetails>
        )}
      </CardBody>
      {dispensation ? (
        <Footer
          data-active={
            prescriptionState === PRESCRIPTION_STATES.AWAITING_DISPENSE
          }
        >
          {prescriptionState === PRESCRIPTION_STATES.DISPENSED && (
            <DispensedPrescriptionFooterItems
              dispensedBy={dispensedBy}
              dispensedAt={dispensation.createdAt}
            />
          )}
          {prescriptionState !== PRESCRIPTION_STATES.DISPENSED &&
            prescriptionState !==
              PRESCRIPTION_STATES.PAPER_PRESCRIPTION_ISSUED &&
            dispensation.createdAt && (
              <DispensedPrescriptionFooterItems
                dispensedBy={dispensedBy}
                dispensedAt={dispensation.createdAt}
              />
            )}
          {prescriptionState ===
            PRESCRIPTION_STATES.PAPER_PRESCRIPTION_ISSUED && (
            <PaperPrescriptionFooterItems
              issuedBy={dispensedBy}
              issuedAt={dispensation.createdAt}
            />
          )}
          {prescriptionState === PRESCRIPTION_STATES.EXPIRED &&
            !dispensation.createdAt && (
              <ExpiredPrescriptionFooterItems prescribedBy={prescribedBy} />
            )}
          {prescriptionState === PRESCRIPTION_STATES.AWAITING_DISPENSE &&
            !dispensation.createdAt && (
              <UndispensedPrescriptionFooterItems
                quantityToDispense={dispensation.quantity}
                validUntil={prescription.stopsAt}
              />
            )}
        </Footer>
      ) : (
        <LegacyPrescription />
      )}
    </Card>
  );
};
